Scratchpads: New scratchpad
<template>
    <form
        method=""
        action=""
        class="form-group"
        @submit.prevent="onSubmit"
        @keydown="getCurrentForm.errors?.clear($event.target.name)" 
        ref="form"
        autocomplete="off"
    >
        <div id="loader" v-if="isLoading">
            <div class="spinner"></div>
        </div>

        <div class="progress-bar-container">
            <ProgressBar
                :active-step="currentStepNumber"
                :total-steps="totalSteps"
            ></ProgressBar>
        </div>

        <component
            :is="{...currentStepComponent}"
            ref="currentStepRef"
            :course="course"
            :step="currentStep"
            :fields="currentStepFields"
            :form-data="formData[currentStepNumber - 1] || sessionOnLoad"
        ></component>

        <div class="form__buttons form-group py-3">
            <button
                type="button"
                class="btn btn-secondary me-2"
                data-cy="btn-back"
                @click="goBack"
            >
                Go Back
            </button>
            <button class="btn btn-primary" data-cy="btn-next">
                {{ currentStepNumber === totalSteps ? "Submit" : "Next" }}
            </button>
        </div>
        <p
            class="form-error"
            v-if="getCurrentForm && getCurrentForm.errors?.get('application-status')"
            role="alert"
            aria-live="assertive"
        >
            {{ getCurrentForm().errors.get("application-status") }}
        </p>
        <div v-if="supportMessage !== null">
            <h3>{{ supportMessage.title }}</h3>
            <div v-html="supportMessage.items[0]" />
        </div>
    </form>
</template>

<script setup>
import ProgressBar from "./ProgressBar.vue";
import StepEmployment from "./Programmes/GetInto/Application/StepEmployment";
import StepEducation from "./Programmes/GetInto/Application/StepEducation";
import StepHealth from "./Programmes/GetInto/Application/StepHealth";
import StepYourCircumstances from "./Programmes/GetInto/Application/StepYourCircumstances";
import StepHowDidYouHearAboutUs from "./Programmes/GetInto/Application/StepHowDidYouHearAboutUs";
import StepEmergencyContactDetails from "./Programmes/GetInto/Application/StepEmergencyContactDetails";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";

const props = defineProps({
    steps: Array,
    course: String,
    interactionid: String,
    sessionOnLoad: Object,
    supportMessage: Object
})

const currentStepRef = useTemplateRef('currentStepRef')
const formData = ref(Array(props.totalSteps))
const isLoading = ref(false)
const previousStepNumber = ref(currentStepNumber)
const formRef = useTemplateRef('form')
const totalSteps = props.steps.length
const currentStepNumber = ref(1)
let currentStepComponent = ref()

let stepIdToComponentMappings = {
    education: StepEducation,
    employment: StepEmployment,
    health: StepHealth,
    yourCircumstances: StepYourCircumstances,
    howDidYouHearAboutUs: StepHowDidYouHearAboutUs,
    emergencyContactDetails: StepEmergencyContactDetails
};

window.onpopstate = (event) => {
    loadStepFromUrl();
};

loadStepFromUrl();

watch(currentStepNumber, () => {
    currentStep.value = props.steps[currentStepNumber.value - 1]
})

let currentStep = ref(props.steps[currentStepNumber.value - 1])

let stepComponents = computed(() => {
    return props.steps.map((step) => {
        return stepIdToComponentMappings[step.id];
     });
})

currentStepComponent = computed(() => {
    return stepComponents.value[currentStepNumber.value - 1]
}) 

const currentStepFields = computed(() => {
    let originalFields = currentStep.value.fields;

    for (const fieldName in originalFields) {
        let field = originalFields[fieldName];

        if (field.validation && Array.isArray(field.validation)) {
            field.required = field.validation.includes("required");
            if (
                field.validation.filter((s) =>
                    s.includes("required_if")
                ).length > 0
            ) {
                let validatedFieldValues = field.validation
                    .find((s) => s.includes("required_if"))
                    .replace("required_if:", "")
                    .split(",");
                field.conditionalRequiredFieldName =
                    validatedFieldValues[0];
                field.conditionalRequiredFieldValues = validatedFieldValues.slice(
                    1
                );
            }
        }
    }
    return originalFields;
})

nextTick(() => {
    window.dataLayer = window.dataLayer || [];
})

function getCurrentForm() {
    return currentStepRef.value.formRef;
}

function loadStepFromUrl() {
    const url = new URL(window.location);
    let parsedStepNumber = Number.parseInt(
        url.searchParams.get("step")
    );

    currentStepNumber.value = parsedStepNumber || 1;
}

function addStepToUrl() {
    const url = new URL(window.location);
    url.searchParams.set("step", currentStepNumber.value);
    window.history.pushState({}, "", url);
}

function scrollToBeginning() {
    const form = formRef.value;

    if (form) {
        const { top } = form.getBoundingClientRect();
        const offsetY = Math.max(0, window.pageYOffset + top - 30);
        window.scrollTo(0, offsetY);
    }
}

function scrollToFirstError() {
    const form = getCurrentForm();

    // If we don't have a form or errors, don't go any further.
    if(!form || !form.errors.any()) {
        return;
    }

    const error = document.getElementsByClassName('form-error')[0];

    if(error) {
        // Find the parent element (question) of the error class.
        const parent = error.parentNode;
        const { top } = parent.getBoundingClientRect();

        // Scroll to just above the parent element so we've got clear context.
        const offsetY = Math.max(0, window.pageYOffset + top - 30);
        window.scrollTo({ top: offsetY, behavior: 'smooth' });
    }
}

function onSubmit() {
    isLoading.value = true;

    if(currentStepNumber.value !== totalSteps) {
        window.dataLayer.push({'event': 'getInto_application_step_' + currentStepNumber.value + '_submitted'});
    } else {
        window.dataLayer.push({'event': 'getInto_application_form_completed'});
    }

    formData[
        currentStepNumber.value - 1
    ] = getCurrentForm().data();

    return axios
        .post(`/courses/${props.course}/apply/${currentStep.value.id}/${props.interactionid}`, getCurrentForm().data())
        .then(() => {
            goForward();
        })
        .catch((err) => {
            console.log(err);
            if (err.response && err.response.status === 422) {
                getCurrentForm().errors.record(err.response.data.errors);
                scrollToFirstError()
            } else {
                window.location.href = "/site-error"
            }

        })
        .finally(() => (isLoading.value = false));
}

function goForward() {
    if (currentStepNumber.value < totalSteps) {
        currentStepNumber.value++;
        addStepToUrl();
        scrollToBeginning();
    } else {
        window.location.href = "/form-completion-getinto";
    }
}


const goBack = () => {
    previousStepNumber.value = currentStepNumber.value - 1;
    window.dataLayer.push({'event': 'getInto_application_step_' + previousStepNumber.value + '_returned'})
    if (currentStepNumber.value <= 1) {
        window.location.href = `/${props.course}-about-you`;
    } else {
        currentStepNumber.value--;
        addStepToUrl();
        scrollToBeginning();
    }
}
    
</script>
